import {
  Configuration,
  Custom,
  DvmData,
  DvmStyles,
  LegalTextData,
  Theme,
  Tutorial,
} from '@core/models';
import { seatSelectionDesktopTutorialSteps } from '@shared/tutorial/seat-selection/steps';

const custom: Custom = {
  paymentPage: {
    captureAddress: true,
    capturePhone: true,
  },
};

const theme: Theme = {
  'default-color': '00 00 00',
  'main-color': '00 00 00',
  'secondary-color': '187 13 48',
  'accent-color': '187 13 48',
  'tertiary-color': '242 246 250',
  red: '189 48 57',
  success: '86 163 67',
  'success-off-white': '236 246 232',
};

const dvmData: DvmData = {
  venueId: 'nam-us-10185-nationals',
  mapId: 'main_venuemap',
  miniMapId: 'main_venuemap_minimap',
  miniMapEnabled: false,
  gaSectorsIdArray: [],
  gaAdaIdArray: [],
  sectionOffsets: {},
  limitedZoom: false,
};

const dvmStyles: DvmStyles = {
  'seat-available-none-fill': '25 149 255',
  'seat-available-none-stroke': '0 90 156',
  'seat-available-disabled-fill': '25 149 255',
  'seat-available-disabled-stroke': '0 90 156',
  'seat-unavailable-none': '214 214 214',
  'seat-available-pending': '230 145 56',
  'section-available-normal-fill': '25 149 255',
  'section-available-normal-stroke': '0 90 156',
  'section-available-normal-group1-fill': '255 77 77',
  'section-available-normal-group1-stroke': '255 77 77',
  'section-available-hover-fill': '0 90 156',
  'section-available-hover-stroke': '255 255 255',
  'section-selected-normal-fill': '225 56 63',
  'section-selected-normal-stroke': '225 56 63',
  'section-selected-hover-stroke': '255 255 255',
  'section-nh-available-normal-fill': '229 154 65',
  'section-nh-available-normal-stroke': '229 154 65',
  'section-nh-available-hover-fill': '229 154 65',
  'section-nh-available-hover-stroke': '229 154 65',
  'section-nh-unavailable-normal-fill': 'transparent',
  'section-nh-unavailable-normal-stroke': 'transparent',
  'section-nh-unavailable-hover-fill': 'transparent',
  'section-nh-unavailable-hover-stroke': 'transparent',
};

const seatSelectionDesktopTutorial: Tutorial = {
  sectionAvailabilityIds: [
    '1254',
    '1256',
    '1258',
    '1261',
    '1074',
    '1076',
    '1078',
    '1080',
    '1066',
  ],
  availableSeatsQuantity: 240,
  selectedSeatsQuantity: 30,
  allowClose: false,
  doneBtnText: 'End Tutorial',
  nextBtnText: 'Next',
  popoverClass: 'driverjs-theme',
  prevBtnText: 'Back',
  steps: seatSelectionDesktopTutorialSteps,
};

const seatSelectionMobileTutorial: Tutorial = {
  ...seatSelectionDesktopTutorial,
  steps: [
    ...seatSelectionDesktopTutorial.steps.slice(0, 4),
    {
      ...seatSelectionDesktopTutorial.steps[4],
      popover: {
        ...seatSelectionDesktopTutorial.steps[4].popover,
        description:
          '5. Click a section to preview a view from that section. Click “Select“ to find your seats in that particular section or click “Open Seat Map“ to manually select seats in that section.',
      },
    },
    {
      ...seatSelectionDesktopTutorial.steps[5],
      popover: {
        ...seatSelectionDesktopTutorial.steps[5].popover,
        description:
          '6. Selected seats appear red with a checkmark. If needed, change your selection by clicking each seat.',
      },
    },
    ...seatSelectionDesktopTutorial.steps.slice(6),
  ],
};

const checkoutTutorial: Tutorial = {
  ...seatSelectionDesktopTutorial,
  steps: [
    {
      element: '#ticket-details',
      popover: {
        description: '1. Review  your ticket information.',
      },
    },
    {
      element: '#group-outing',
      popover: {
        description:
          '2. Enhance your group outing with an optional upgraded ticket.',
      },
    },
    {
      element: '#cart-summary',
      popover: {
        description:
          '3. Review the details of your group ticket pricing. Click “Proceed to Payment” to complete your purchase.',
      },
    },
  ],
};

export const WashingtonNationalsConfig: Configuration = {
  id: 'washingtonnationals',
  clientName: 'Washington Nationals',
  appName: 'Washington Nationals',
  city: 'Washington D.C.',
  favicon: '/assets/img/washingtonnationals/favicon.png',
  logo: '/assets/img/washingtonnationals/favicon.png',
  phone: '202-675-NATS (6287)',
  email: 'Ticket.info@nationals.com',
  paymentMethod: 'braintree',
  rainout: false,
  upsells: true,
  clientWebUrl: 'https://www.nationals.com/',
  clientWebText: 'https://www.nationals.com/',
  termsOfUseUrl: 'https://www.mlb.com/official-information/terms-of-use',
  clientBallParkUrl:
    'https://www.mlb.com/nationals/apps/ballpark?partnerId=redirect-chc-ballparkapp',
  clientBallParkText: 'nationals.com/BallparkApp',
  mlbShopUrl:
    'https://www.mlbshopeurope.com/en/washington-nationals/t-14553163+z-871253-930065732',
  merchandiseWebUrl: '',
  rainoutInsuranceUrl: '',
  termsAndConditionsUrl:
    'https://www.mlb.com/nationals/tickets/terms-and-conditions?_gl=1*olgww1*_gcl_au*MjA0NDE3MzE5MS4xNzI5MDg3NDIw',
  analytics: '',
  adas: true,
  custom,
  theme,
  dvmData,
  dvmStyles,
  cartSummaryText: 'Here you can see your new requested tickets',
  tutorial: {
    seatSelection: {
      desktop: seatSelectionDesktopTutorial,
      mobile: seatSelectionMobileTutorial,
    },
    checkout: { desktop: checkoutTutorial, mobile: checkoutTutorial },
  },
  extraCheckbox: {
    active: false,
    required: true,
    text: '',
  },
  summaryPage: {
    image1: '/assets/img/washingtonnationals/favicon.png',
    image2: '',
  },
  get legalText(): LegalTextData {
    return {
      links: {
        termsLink: {
          href: this.termsAndConditionsUrl,
          text: 'Terms and Conditions',
          target: '_blank',
        },
      },
      text: [
        `Pricing and discounts may vary based on seating location and game. Seating and pricing subject to day-of-game and market-based demand pricing changes.
        All seating is subject to availability. Promotions are subject to change without notice. Some restrictions apply. Cannot be combined with other offers.
        Ticket brokers are not eligible for this offer. Tickets purchased through this offer are not valid for resale.
        The Nationals reserve the right to cancel orders in violation of Nationals resale policy. All sales are final. There are no refunds, exchanges or cancellations.`,
        `*The Nationals reserve the right to change any and all 2026 access, benefits, promotions, seating locations and events
        (including, without limitation, location, dates and times) without notice based upon: 1) federal, city, state and/or local government orders, mandates or restrictions; and 2)
        other reason as determined by the Nationals and/or Major League Baseball in their sole discretions.*`,
        `Ticket brokers are not eligible for this offer. All sales are final. There are no refunds, exchanges or cancellations.`,
      ],
    };
  },
  defaultTooltipText:
    'Ticket brokers are not eligible for this offer. All sales are final. There are no refunds, exchanges or cancellations.',
  teamId: '120',
  metaPixelAnalytics: false,
};
